import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Award,
  Plus,
  Trash2,
  Share2,
  Copy,
  Check,
  ExternalLink,
  Edit,
  Eye,
  Code,
  Sparkles,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EntitlementGate from "@/components/EntitlementGate";

export default function ProofBuilder({ user }) {
  const [proofs, setProofs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProof, setSelectedProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // New proof form
  const [newProof, setNewProof] = useState({
    project_id: "",
    title: "",
    highlights: "",
    metrics: "",
    testimonial: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [proofsRes, projectsRes] = await Promise.all([
        api.get("/proofs").catch(() => ({ data: { proofs: [] } })),
        api.get("/projects").catch(() => ({ data: [] }))
      ]);
      setProofs(proofsRes.data.proofs || []);
      setProjects(projectsRes.data || []);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  const generateProof = async () => {
    if (!newProof.title.trim()) {
      toast.error("Please enter a title for the case study");
      return;
    }
    
    setGenerating(true);
    try {
      const res = await api.post("/proofs/generate", {
        project_id: newProof.project_id || undefined,
        title: newProof.title,
        highlights: newProof.highlights,
        metrics: newProof.metrics ? { note: newProof.metrics } : {},
        testimonial: newProof.testimonial
      });
      
      const newProofData = {
        id: res.data.proof_id,
        title: res.data.title,
        body_md: res.data.body_md,
        visibility: "private",
        created_at: new Date().toISOString()
      };
      
      setProofs([newProofData, ...proofs]);
      setSelectedProof(newProofData);
      setShowNewForm(false);
      setNewProof({ project_id: "", title: "", highlights: "", metrics: "", testimonial: "" });
      toast.success("Case study generated!");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to generate case study");
    } finally {
      setGenerating(false);
    }
  };

  const updateProof = async (updates) => {
    if (!selectedProof) return;
    
    try {
      await api.put(`/proofs/${selectedProof.id}`, updates);
      
      const updated = { ...selectedProof, ...updates };
      setSelectedProof(updated);
      setProofs(proofs.map(p => p.id === selectedProof.id ? updated : p));
      toast.success("Saved!");
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const deleteProof = async (proofId) => {
    if (!confirm("Delete this case study?")) return;
    
    try {
      await api.delete(`/proofs/${proofId}`);
      setProofs(proofs.filter(p => p.id !== proofId));
      if (selectedProof?.id === proofId) {
        setSelectedProof(null);
      }
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const shareProof = async () => {
    if (!selectedProof) return;
    
    try {
      const res = await api.post(`/proofs/${selectedProof.id}/share`);
      const shareUrl = `${window.location.origin}${res.data.url}`;
      
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      setSelectedProof({ ...selectedProof, share_token: res.data.token, visibility: "public" });
      toast.success("Share link copied!");
    } catch (e) {
      toast.error("Failed to create share link");
    }
  };

  const getEmbedCode = async () => {
    if (!selectedProof) return;
    
    try {
      const res = await api.get(`/proofs/${selectedProof.id}/embed`);
      await navigator.clipboard.writeText(res.data.html);
      toast.success("Embed code copied!");
    } catch (e) {
      toast.error("Failed to get embed code");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <EntitlementGate feature="proofs" user={user}>
      <div className="space-y-6" data-testid="proof-builder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
              <Award className="w-6 h-6 text-indigo-400" />
              Proof Builder
            </h1>
            <p className="text-indigo-400 text-sm mt-1">
              Generate professional case studies from your projects
            </p>
          </div>
          
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Case Study
          </button>
        </div>

        {/* New Proof Form */}
        {showNewForm && (
          <Card className="bg-slate-800/50 border-indigo-500/30">
            <CardHeader>
              <CardTitle className="text-indigo-500">Generate Case Study</CardTitle>
              <CardDescription className="text-indigo-400">
                Provide details and let AI create a compelling case study
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Title *</label>
                  <input
                    type="text"
                    value={newProof.title}
                    onChange={(e) => setNewProof({ ...newProof, title: e.target.value })}
                    placeholder="e.g., E-commerce Redesign for TechCo"
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Link to Project</label>
                  <Select
                    value={newProof.project_id || "none"}
                    onValueChange={(v) => setNewProof({ ...newProof, project_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Select project (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="none" className="text-slate-400">None</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id} className="text-slate-400">
                          {p.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-indigo-400 mb-1">Key Highlights</label>
                <Textarea
                  value={newProof.highlights}
                  onChange={(e) => setNewProof({ ...newProof, highlights: e.target.value })}
                  placeholder="What were the main achievements? What made this project special?"
                  className="min-h-[80px] bg-slate-900/50 border-slate-600 text-indigo-500 placeholder:text-slate-400"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Metrics/Results</label>
                  <input
                    type="text"
                    value={newProof.metrics}
                    onChange={(e) => setNewProof({ ...newProof, metrics: e.target.value })}
                    placeholder="e.g., 40% increase in conversions"
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Client Testimonial</label>
                  <input
                    type="text"
                    value={newProof.testimonial}
                    onChange={(e) => setNewProof({ ...newProof, testimonial: e.target.value })}
                    placeholder="Optional quote from the client"
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={generateProof}
                  disabled={generating}
                  className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Case Study
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowNewForm(false)}
                  className="px-6 py-3 text-indigo-400 hover:text-indigo-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Proofs List */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400 uppercase tracking-wide">
              Case Studies ({proofs.length})
            </h3>
            
            {proofs.length === 0 ? (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-8 text-center">
                  <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-indigo-400 text-sm">No case studies yet</p>
                </CardContent>
              </Card>
            ) : (
              proofs.map((proof) => (
                <button
                  key={proof.id}
                  onClick={() => setSelectedProof(proof)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedProof?.id === proof.id
                      ? "bg-indigo-500/10 border-indigo-500/50"
                      : "bg-slate-800/30 border-slate-700/50 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-indigo-500 line-clamp-1">{proof.title}</span>
                    <Badge className={
                      proof.visibility === "public" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-slate-500/20 text-indigo-400"
                    }>
                      {proof.visibility}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400">
                    {new Date(proof.created_at).toLocaleDateString()}
                  </p>
                </button>
              ))
            )}
          </div>

          {/* Proof Editor */}
          <div className="lg:col-span-2">
            {selectedProof ? (
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-indigo-500">{selectedProof.title}</CardTitle>
                      <CardDescription className="text-indigo-400">
                        {selectedProof.visibility === "public" ? "Publicly shared" : "Private"}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={shareProof}
                        className="p-2 text-indigo-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                        title="Share"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-400" /> : <Share2 className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={getEmbedCode}
                        className="p-2 text-indigo-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors"
                        title="Get embed code"
                      >
                        <Code className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteProof(selectedProof.id)}
                        className="p-2 text-indigo-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <label className="block text-sm text-indigo-400 mb-2">Summary (for previews)</label>
                    <input
                      type="text"
                      value={selectedProof.summary || ""}
                      onChange={(e) => updateProof({ summary: e.target.value })}
                      placeholder="Brief one-line summary"
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-indigo-400 mb-2">Content (Markdown)</label>
                    <Textarea
                      value={selectedProof.body_md || ""}
                      onChange={(e) => updateProof({ body_md: e.target.value })}
                      className="min-h-[400px] bg-slate-900/50 border-slate-600 text-indigo-500 font-mono text-sm"
                    />
                  </div>
                  
                  {selectedProof.share_token && (
                    <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm text-green-400 flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" />
                        Share URL: {window.location.origin}/proof/{selectedProof.share_token}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-16 text-center">
                  <Eye className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-indigo-400">Select a case study to view and edit</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </EntitlementGate>
  );
}
